import { message } from "antd";
import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import api from '../api/axios'

const UXContext = createContext(null);

export function UXProvider({ children }) {
    const [isBeta, setIsBeta] = useState(null);
    const [loading, setLoading] = useState(true);
    const [profileRevision, setProfileRevision] = useState(0);
    const didRun = useRef(false);
    const confirmedBetaRef = useRef(null);

    // Records a server-confirmed profile state and bumps the revision whenever
    // it differs from the previously confirmed one.
    const confirmBeta = useCallback((value) => {
        setIsBeta(value);
        if (confirmedBetaRef.current === value) return;
        const isFirst = confirmedBetaRef.current === null;
        confirmedBetaRef.current = value;
        if (!isFirst) setProfileRevision((revision) => revision + 1);
    }, []);

    const fetchUXStatus = useCallback(async () => {
        try {
            setLoading(true);
            const { data } = await api.get("/accounts/ux-status/");
            confirmBeta(data.is_beta);
        } catch (err) {
            console.error("Failed to fetch UX status", err);
            message.error("Could not load user settings. Contact support if issue persists.");
        } finally {
            setLoading(false);
        }
    }, [confirmBeta]);

    useEffect(() => {
        if (didRun.current) return;
        didRun.current = true;
        fetchUXStatus();
    }, [fetchUXStatus]);

    const toggleBeta = async () => {
        if (isBeta === null) return;
        try {
            setIsBeta(!isBeta); // optimistic UI
            const { data } = await api.post("/accounts/toggle-beta/");
            confirmBeta(data.is_beta);
        } catch (err) {
            message.error("Failed to subscribe to beta. Contact support if issue persists.");
            setIsBeta((prev) => !prev);
        }
    };

    const refreshUX = async () => {
        await fetchUXStatus();
    };

    return (
        <UXContext.Provider
            value={{ isBeta, toggleBeta, loading, refreshUX, profileRevision }}
        >
            {children}
        </UXContext.Provider>
    );
}

export function useUX() {
    return useContext(UXContext);
}
